import {
    GRID_ACTIONS_COLUMN_TYPE,
    type GridColumns,
    type GridEnrichedColDef
} from "@mui/x-data-grid-premium";
import { when } from "util/support";
import { renderers } from "ui/widgets/Table";
import withChange from "./withChange";
import { Column, DiffColumn, HistoryRepository } from "../repository";
import { Reason, HistoryStatus } from "./columns";

type Field = Column | typeof GRID_ACTIONS_COLUMN_TYPE;

export const useColumns = () => {
    const colDef: Partial<GridEnrichedColDef> = {
        align: 'center',
        flex: 1,
        minWidth: 200,
        headerAlign: 'center',
        disableColumnMenu: true,
        sortable: false,
        disableExport: true,
        disableReorder: true,
        renderCell: withChange()
    };

    const getColumnDef = (field: Field) => (
        new Map<Field, GridEnrichedColDef & {
            readonly jsonable?: boolean;
        }>()
            .set(Column.RefId, {
                ...colDef,
                field: Column.RefId,
                headerName: 'Reference ID',
                align: 'left',
                minWidth: 300
            })
            .set(Column.Action, {
                ...colDef,
                field: Column.Action,
                headerName: 'Action',
                minWidth: 220
            })
            .set(Column.HistoryCreatedAt, {
                ...colDef,
                field: Column.HistoryCreatedAt,
                headerName: 'History Created At'
            })
            .set(Column.DateTime, {
                ...colDef,
                description: 'Also known as "Updated At"',
                field: Column.DateTime,
                headerName: 'Updated At'
            })
            .set(Column.Status, {
                ...colDef,
                field: Column.Status,
                headerName: 'Status',
                renderCell: withChange(renderers.TransactionStatus)
            })
            .set(Column.FundStatus, {
                ...colDef,
                field: Column.FundStatus,
                headerName: 'Fund Status',
                minWidth: 250,
                renderCell: withChange(renderers.FundStatus)
            })
            .set(Column.SettlementStatus, {
                ...colDef,
                field: Column.SettlementStatus,
                headerName: 'Settlement Status',
                renderCell: withChange(renderers.SettlementStatus)
            })
            .set(Column.IsSettled, {
                ...colDef,
                field: Column.IsSettled,
                headerName: 'Settled',
                renderCell: withChange(renderers.BooleanStatus)
            })
            .set(Column.SettledDate, {
                ...colDef,
                field: Column.SettledDate,
                headerName: 'Settled Date'
            })
            .set(Column.SettlementStatusDateTime, {
                ...colDef,
                field: Column.SettlementStatusDateTime,
                headerName: 'Settlement Status Date Time'
            })
            .set(Column.UpdatedAt, {
                ...colDef,
                field: Column.UpdatedAt,
                headerName: 'Updated At',
                hide: true
            })
            .set(Column.LocalTime, {
                ...colDef,
                field: Column.LocalTime,
                headerName: 'Local Time'
            })
            .set(Column.TransactionDecision, {
                ...colDef,
                field: Column.TransactionDecision,
                headerName: 'Decision',
                jsonable: true
            })
            .set(Column.TransactionRequest, {
                ...colDef,
                field: Column.TransactionRequest,
                headerName: 'Request',
                jsonable: true
            })
            .set(Column.TransactionResponse, {
                ...colDef,
                field: Column.TransactionResponse,
                headerName: 'Response',
                jsonable: true
            })
            .set(Column.ReasonCode, {
                ...colDef,
                field: Column.ReasonCode,
                headerName: 'Reason Code'
            })
            .set(Column.SucceededAt, {
                ...colDef,
                field: Column.SucceededAt,
                headerName: 'Succeeded time'
            })
            .set(Column.FailedAt, {
                ...colDef,
                field: Column.FailedAt,
                headerName: 'Failed time'
            })
            .set(Column.RejectedAt, {
                ...colDef,
                field: Column.RejectedAt,
                headerName: 'Rejection time'
            })
            .set(Column.RefundedAt, {
                ...colDef,
                field: Column.RefundedAt,
                headerName: 'Refunded time'
            })
            .set(Column.ChargedBackAt, {
                ...colDef,
                field: Column.ChargedBackAt,
                headerName: 'Charged Back'
            })
            .set(Column.FundsWaitingAt, {
                ...colDef,
                field: Column.FundsWaitingAt,
                headerName: 'Funds Waiting'
            })
            .set(Column.FundsReceivedAt, {
                ...colDef,
                field: Column.FundsReceivedAt,
                headerName: 'Funds Received'
            })
            .set(Column.FundsMissingAt, {
                ...colDef,
                field: Column.FundsMissingAt,
                headerName: 'Funds Missing'
            })
            .set(Column.FundsSentAt, {
                ...colDef,
                field: Column.FundsSentAt,
                headerName: 'Funds Sent'
            })
            .set(Column.FundsPartiallySentAt, {
                ...colDef,
                field: Column.FundsPartiallySentAt,
                headerName: 'Funds Partially Sent'
            })
            .set(Column.FundsNotExpectedAt, {
                ...colDef,
                field: Column.FundsNotExpectedAt,
                headerName: 'Funds Not Expected'
            })
            .set(Column.FundsRefundedAt, {
                ...colDef,
                field: Column.FundsRefundedAt,
                headerName: 'Funds Refunded'
            })
            .set(Column.FundsPartiallyRefundedAt, {
                ...colDef,
                field: Column.FundsPartiallyRefundedAt,
                headerName: 'Funds Partially Refunded'
            })
            .set(Column.FundsPartiallySentRefundedAt, {
                ...colDef,
                field: Column.FundsPartiallySentRefundedAt,
                headerName: 'Funds Partially Sent Refunded'
            })
            .set(Column.FundsPartiallyRefundedSentAt, {
                ...colDef,
                field: Column.FundsPartiallyRefundedSentAt,
                headerName: 'Funds Partially Refunded Sent'
            })
            .set(Column.FundsFullySentAt, {
                ...colDef,
                field: Column.FundsFullySentAt,
                headerName: 'Funds Fully Sent'
            })
            .set(Column.FundsFullyRefundedAt, {
                ...colDef,
                field: Column.FundsFullyRefundedAt,
                headerName: 'Funds Fully Refunded'
            })
            .set(Column.FundsFullySentRefundedAt, {
                ...colDef,
                field: Column.FundsFullySentRefundedAt,
                headerName: 'Funds Fully Sent Refunded'
            })
            .set(Column.FundsFullyRefundedSentAt, {
                ...colDef,
                field: Column.FundsFullyRefundedSentAt,
                headerName: 'Funds Fully Refunded Sent'
            })
            .set(Column.FundsDeductedAt, {
                ...colDef,
                field: Column.FundsDeductedAt,
                headerName: 'Deducted'
            })
            .set(Column.Reason, {
                ...colDef,
                minWidth: 80,
                field: Column.Reason,
                headerName: 'Reason',
                pinnable: true,
                renderCell: withChange(Reason)
            })
            .set(GRID_ACTIONS_COLUMN_TYPE, {
                ...colDef,
                minWidth: 30,
                pinnable: true,
                field: GRID_ACTIONS_COLUMN_TYPE,
                headerName: '',
                renderCell: HistoryStatus
            })
            .get(field)!
    );

    const getPinnedColumns = (columns: GridColumns) =>
        columns.reduce((acc, column) => (
            when(
                column.pinnable,
                () => [
                    ...acc,
                    column.field
                ],
                acc
            )
        ), []);

    const withAvailableColumns = (
        getColumns: () => GridColumns,
        columns: HistoryRepository['columns']
    ) => getColumns()
        .reduce((acc, column) => (
            when(
                columns.includes(column.field as DiffColumn),
                () => [
                    ...acc,
                    column
                ],
                acc
            )
        ), [] as GridColumns);

    const getScalarColumns = (): GridColumns => [
        getColumnDef(Column.RefId),
        getColumnDef(Column.Action),
        getColumnDef(Column.HistoryCreatedAt),
        getColumnDef(Column.DateTime),
        getColumnDef(Column.Status),
        getColumnDef(Column.FundStatus),
        getColumnDef(Column.SettlementStatus),
        getColumnDef(Column.IsSettled),
        getColumnDef(Column.SettledDate),
        getColumnDef(Column.SettlementStatusDateTime),
        getColumnDef(Column.UpdatedAt),
        getColumnDef(Column.LocalTime),
        getColumnDef(Column.Reason),
        getColumnDef(Column.ReasonCode)
    ];

    const getObjectColumns = (): GridColumns => [
        getColumnDef(Column.TransactionDecision),
        getColumnDef(Column.TransactionRequest),
        getColumnDef(Column.TransactionResponse)
    ];

    return {
        withAvailableColumns,
        getColumnDef,
        getScalarColumns,
        getObjectColumns,
        getPinnedColumns,
    };
};

export default useColumns;
